import type { MarketingNote } from '../../../../api/venue-marketing';
import { ShowMore } from '../../primitives/ShowMore';

import { FormAlert } from './FormAlert';
import type { NoteListCopy } from './marketing-note-lists';
import { MarketingNoteItem } from './MarketingNoteItem';

interface MarketingNoteListBodyProps {
  venueId: string;
  regionName: string | null;
  copy: NoteListCopy;
  notes: MarketingNote[];
  total: number;
  pageSize: number | undefined;
  status: 'pending' | 'error' | 'success';
  loadError: string | null;
  loadingMore: boolean;
  canToggle: boolean;
  /** Whether an empty list should invite the caller to add the first entry. */
  canAdd: boolean;
  onRetry: () => void;
  onMore: () => void;
  onError: (message: string) => void;
}

const empty = {
  padding: '12px 14px',
  marginTop: 10,
  borderRadius: 10,
  border: '1px dashed var(--nb-border-2)',
  fontSize: 12,
  color: 'var(--nb-muted)',
  lineHeight: 1.5,
} as const;

/** The loaded, loading, failed or empty state of one list, with its paging footer. */
export function MarketingNoteListBody(props: MarketingNoteListBodyProps) {
  const { copy, notes, total, status, loadError } = props;

  if (status === 'pending') {
    return (
      <div role="status" style={{ ...empty, border: 'none', padding: '10px 0' }}>
        Loading…
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <FormAlert message={loadError ?? copy.empty} />
        <button
          type="button"
          className="nb-btn nb-btn-ghost"
          onClick={props.onRetry}
          style={{ alignSelf: 'flex-start', height: 24, fontSize: 11 }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (notes.length === 0) {
    return (
      <div style={empty}>
        {copy.empty}
        {props.canAdd && copy.emptyForEditors ? ` ${copy.emptyForEditors}` : ''}
      </div>
    );
  }
  return (
    <>
      <ul style={{ margin: 0, padding: 0 }}>
        {notes.map((note, i) => (
          <MarketingNoteItem
            key={note.id}
            note={note}
            venueId={props.venueId}
            regionName={props.regionName}
            canToggle={props.canToggle}
            last={i === notes.length - 1}
            onError={props.onError}
          />
        ))}
      </ul>
      <ShowMore
        shown={notes.length}
        total={total}
        step={props.pageSize}
        onMore={props.onMore}
        busy={props.loadingMore}
      />
    </>
  );
}
